import { useEffect, useState } from "react";
import { Wallet, Package, Download, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";

const PAY_LABEL = { paid: ["Captured", "bg-emerald-100 text-emerald-700"], pending: ["Pending", "bg-amber-100 text-amber-700"], authorized: ["Authorized", "bg-blue-100 text-blue-700"], failed: ["Failed", "bg-rose-100 text-rose-700"] };
const REFUND_LABEL = { refund_pending: ["Refund requested", "text-amber-600"], refund_initiated: ["Refund initiated", "text-blue-600"], refunded: ["Refunded", "text-emerald-600"], reconciliation_required: ["Under reconciliation", "text-slate-500"] };
const REFUND_STATES = ["refund_pending", "refund_initiated", "refunded", "reconciliation_required"];
const amt = (o) => Math.round(o.net_amount || o.subtotal || o.total || 0);

export default function PaymentsPage() {
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("all");
  const [year, setYear] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/marketplace/my-orders").then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([])); }, []);
  if (!orders) return null;

  const years = [...new Set(orders.map((o) => new Date(o.created_at).getFullYear()))].sort((a, b) => b - a);
  const quick = (k) => { const now = new Date(); const fmt = (d) => d.toISOString().slice(0, 10);
    if (k === "7") { setFrom(fmt(new Date(Date.now() - 6 * 864e5))); setTo(fmt(now)); }
    else if (k === "30") { setFrom(fmt(new Date(Date.now() - 29 * 864e5))); setTo(fmt(now)); }
    else if (k === "month") { setFrom(fmt(new Date(now.getFullYear(), now.getMonth(), 1))); setTo(fmt(now)); }
    else if (k === "year") { setFrom(fmt(new Date(now.getFullYear(), 0, 1))); setTo(fmt(now)); } };

  const filtered = orders.filter((o) => {
    const d = new Date(o.created_at);
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(new Date(to).setHours(23, 59, 59))) return false;
    if (year !== "all" && d.getFullYear() !== Number(year)) return false;
    if (status === "refunded" && !REFUND_STATES.includes(o.refund_status)) return false;
    if (status !== "all" && status !== "refunded" && o.payment_status !== status) return false;
    if (search.trim() && !(o.number || "").toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });
  const refunds = filtered.filter((o) => REFUND_STATES.includes(o.refund_status));

  const exportCsv = () => {
    if (!filtered.length) { toast.error("No payments to export"); return; }
    const rows = [["Order", "Date", "Amount (INR)", "Payment Status", "Refund Status"],
      ...filtered.map((o) => [o.number, new Date(o.created_at).toLocaleString(), amt(o), o.payment_status || "", o.refund_status || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "suffle-payment-history.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payment History" icon={Wallet} subtitle="Payments for your marketplace orders. We never store card, CVV or UPI details." />

      <div className="mb-4 space-y-3" data-testid="payments-filters">
        <div className="flex flex-wrap gap-1.5">
          {[["all", "All"], ["paid", "Paid"], ["pending", "Pending"], ["authorized", "Authorized"], ["failed", "Failed"], ["refunded", "Refunded"]].map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`pay-filter-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${status === k ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order #" data-testid="payments-search" className="min-w-[160px] flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" />
          <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="payments-year" className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600"><option value="all">All years</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
          {[["7", "7 days"], ["30", "30 days"], ["month", "This month"], ["year", "This year"]].map(([k, l]) => <button key={k} onClick={() => quick(k)} data-testid={`pay-quick-${k}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-400">{l}</button>)}
          <div><label className="text-xs text-slate-500">From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pay-date-from" className="mt-0.5 block rounded-lg border border-slate-200 px-3 py-1.5 text-sm" /></div>
          <div><label className="text-xs text-slate-500">To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="pay-date-to" className="mt-0.5 block rounded-lg border border-slate-200 px-3 py-1.5 text-sm" /></div>
          <button onClick={exportCsv} data-testid="pay-export" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white"><Download className="h-3.5 w-3.5" /> Download statement</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="payments-empty">
          <Wallet className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">{orders.length === 0 ? "No payments yet" : "No payments match these filters"}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="payments-list">
          {filtered.map((o) => {
            const pay = PAY_LABEL[o.payment_status] || [o.payment_status || "—", "bg-slate-100 text-slate-600"];
            const rf = o.refund_status ? (REFUND_LABEL[o.refund_status] || [o.refund_status, "text-slate-500"]) : null;
            return (
              <div key={o.order_id || o.number} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`payment-${o.order_id || o.number}`}>
                <Package className="h-5 w-5 shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">Order {o.number}</p>
                  <p className="text-xs text-slate-400">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}{o.suborders?.length ? ` · ${o.suborders.length} seller(s)` : ""}</p>
                  {rf && <p className={`text-xs font-medium ${rf[1]}`}>{rf[0]}</p>}
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-bold text-slate-900">₹{amt(o)}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${pay[1]}`}>{pay[0]}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {refunds.length > 0 && (
        <div className="mt-6" data-testid="refund-history">
          <h2 className="mb-2 flex items-center gap-1.5 text-lg font-bold text-slate-800"><RotateCcw className="h-4 w-4 text-slate-400" /> Refund History</h2>
          <div className="space-y-2">
            {refunds.map((o) => { const rf = REFUND_LABEL[o.refund_status] || [o.refund_status, "text-slate-500"]; return (
              <div key={`rf-${o.order_id || o.number}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`refund-${o.order_id || o.number}`}>
                <div><p className="font-semibold text-slate-800">Order {o.number}</p><p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleDateString()}</p></div>
                <div className="text-right"><p className="font-bold text-slate-900">₹{amt(o)}</p><span className={`text-xs font-medium ${rf[1]}`}>{rf[0]}</span></div>
              </div>
            ); })}
          </div>
        </div>
      )}
    </div>
  );
}
